/**
 * Check-in mobile com leitor de código de barras (Code128).
 *
 * Usa `html5-qrcode` para abrir câmara, ler o código, e regista a
 * presença na sessão escolhida. Mobile-first (cartões grandes, botões
 * generosos).
 *
 * URL: /admin/cursos/checkin
 */
import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { CheckCircle2, AlertTriangle, Camera, CameraOff, ChevronLeft, Loader2, Download, LogOut, Maximize2, Minimize2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi } from "../api";

const SCAN_REGION_ID = "checkin-scan-region";
// URL relativa para garantir same-origin. Resolve o problema de iOS Safari
// que bloqueia cookies `SameSite=None` em pedidos cross-origin — sintoma:
// login passa mas o `/me` seguinte falha e o utilizador fica num loop.
const API = "";

// ── PWA install prompt management ─────────────────────────────────
let deferredInstallPrompt = null;
function _setupPWA() {
  if (typeof window === "undefined") return;
  // Listen for beforeinstallprompt (Chrome/Edge/Android)
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    window.dispatchEvent(new Event("checkin-installable"));
  });
  // Register Service Worker so the browser considers the app installable
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/checkin-sw.js", { scope: "/admin/cursos/" })
      .catch(() => { /* ignore — install button apenas não aparece */ });
  }
  // Inject manifest link if not present
  if (!document.querySelector('link[rel="manifest"][data-checkin="1"]')) {
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/checkin-manifest.json";
    link.dataset.checkin = "1";
    document.head.appendChild(link);
  }
}

export default function AdminCursoCheckin() {
  const scannerRef = useRef(null);
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const [match, setMatch] = useState(null);   // { ficha, sessoes, checkins }
  const [submitting, setSubmitting] = useState(false);
  const [lastCode, setLastCode] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [meEmail, setMeEmail] = useState("");
  const [authChecked, setAuthChecked] = useState(false);
  const [installable, setInstallable] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [kiosk, setKiosk] = useState(false);
  const wakeLockRef = useRef(null);

  // Auth: tenta cookie checkin; se não, cai para login dedicado
  useEffect(() => {
    fetch(`${API}/api/checkin/auth/me`, { credentials: "include" })
      .then((r) => {
        if (r.ok) return r.json();
        throw new Error("not_authed");
      })
      .then((d) => { setMeEmail(d.email); setAuthChecked(true); })
      .catch(() => {
        // Tenta cookie admin DB — admins continuam a poder usar sem checkin role
        fetch(`${API}/api/admin/auth/me`, { credentials: "include" })
          .then((r) => r.ok ? r.json() : Promise.reject())
          .then((d) => { setMeEmail(d.email); setAuthChecked(true); })
          .catch(() => navigate("/admin/cursos/checkin/login", { replace: true }));
      });
  }, [navigate]);

  useEffect(() => {
    _setupPWA();
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => { setInstallable(false); setInstalled(true); };
    window.addEventListener("checkin-installable", onInstallable);
    window.addEventListener("appinstalled", onInstalled);
    // Detect standalone (already installed)
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) setInstalled(true);
    return () => {
      window.removeEventListener("checkin-installable", onInstallable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const installPWA = async () => {
    if (!deferredInstallPrompt) {
      toast.info("Para instalar no iOS: toque em ‘Partilhar’ → ‘Adicionar ao Ecrã Principal’.");
      return;
    }
    deferredInstallPrompt.prompt();
    const { outcome } = await deferredInstallPrompt.userChoice;
    deferredInstallPrompt = null;
    setInstallable(false);
    if (outcome === "accepted") setInstalled(true);
  };

  const logout = async () => {
    await fetch(`${API}/api/checkin/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/admin/cursos/checkin/login", { replace: true });
  };

  // ── Modo Kiosk ────────────────────────────────────────────────
  const enterKiosk = async () => {
    setKiosk(true);
    // Fullscreen
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: "hide" });
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen(); // iOS Safari
    } catch { /* ignore */ }
    // Screen Wake Lock (mantém ecrã ligado)
    try {
      if ("wakeLock" in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch { /* ignore */ }
  };
  const exitKiosk = async () => {
    setKiosk(false);
    try { if (document.fullscreenElement) await document.exitFullscreen(); } catch { /* ignore */ }
    try { await wakeLockRef.current?.release?.(); wakeLockRef.current = null; } catch { /* ignore */ }
  };
  // Re-adquire wake lock se o utilizador voltar à app
  useEffect(() => {
    const onVis = async () => {
      if (kiosk && document.visibilityState === "visible" && !wakeLockRef.current && "wakeLock" in navigator) {
        try { wakeLockRef.current = await navigator.wakeLock.request("screen"); } catch { /* ignore */ }
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [kiosk]);
  // Sair do kiosk se o user pressionar ESC (browsers saem do fullscreen automaticamente)
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement && kiosk) setKiosk(false); };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [kiosk]);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        if (scannerRef.current.getState() === 2 /* SCANNING */) await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch { /* ignore */ }
    }
    setScanning(false);
  };

  const startScanner = async () => {
    setError("");
    setMatch(null);
    // CRITICAL: tornar o viewport visível ANTES de iniciar a câmara — caso contrário
    // o elemento tem clientWidth=0 (display:none) e o html5-qrcode não consegue
    // anexar o stream de vídeo. Reverte-se caso a câmara falhe.
    setScanning(true);
    // Aguarda 1 frame para o React aplicar o display:block ao container
    await new Promise((r) => requestAnimationFrame(() => r()));

    // Pré-requisitos do browser
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setScanning(false);
      setError("O browser não suporta acesso à câmara. Use Chrome/Safari atualizados em HTTPS.");
      return;
    }
    if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
      setScanning(false);
      setError("Acesso à câmara requer HTTPS. Abra esta página em https://...");
      return;
    }

    const scanner = new Html5Qrcode(SCAN_REGION_ID, {
      verbose: false,
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
    });
    scannerRef.current = scanner;
    const config = { fps: 15, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 };
    const onDecoded = (decoded) => onCodeDetected(decoded, scanner);
    const onFrameErr = () => { /* per-frame ignore */ };

    // 1ª tentativa: facingMode environment (câmara traseira)
    try {
      await scanner.start({ facingMode: { exact: "environment" } }, config, onDecoded, onFrameErr);
      return;
    } catch (e1) {
      console.warn("[checkin] facingMode exact failed:", e1?.name, e1?.message);
    }
    try {
      await scanner.start({ facingMode: "environment" }, config, onDecoded, onFrameErr);
      return;
    } catch (e2) {
      console.warn("[checkin] facingMode environment failed:", e2?.name, e2?.message);
    }
    // 2ª tentativa: enumerar câmaras e escolher a traseira por deviceId (iOS Safari)
    try {
      const cams = await Html5Qrcode.getCameras();
      if (!cams || cams.length === 0) throw new Error("Nenhuma câmara detectada");
      const back = cams.find((c) => /back|rear|traseira|environment/i.test(c.label)) || cams[cams.length - 1];
      await scanner.start({ deviceId: { exact: back.id } }, config, onDecoded, onFrameErr);
      return;
    } catch (e3) {
      console.error("[checkin] camera start failed:", e3);
      setScanning(false);
      const name = e3?.name || "";
      let msg = e3?.message || "Não foi possível abrir a câmara.";
      if (name === "NotAllowedError" || /permission/i.test(msg)) {
        msg = "Permissão de câmara negada. Active a câmara nas definições do site no browser e recarregue.";
      } else if (name === "NotFoundError" || /no camera|nenhuma câmara/i.test(msg)) {
        msg = "Nenhuma câmara encontrada neste dispositivo.";
      } else if (name === "NotReadableError") {
        msg = "Câmara em uso por outra aplicação. Feche outras apps e tente novamente.";
      } else if (name === "OverconstrainedError") {
        msg = "Câmara traseira indisponível. Tente novamente — será usada a frontal.";
      }
      setError(`${msg} (${name || "erro"})`);
    }
  };

  const onCodeDetected = async (code, scanner) => {
    if (!code || code === lastCode) return;
    setLastCode(code);
    try { await scanner.pause(true); } catch { /* ignore */ }
    await lookup(code);
  };

  const lookup = async (code) => {
    try {
      const res = await adminApi.cursoCheckinLookup(code);
      setMatch(res);
    } catch (e) {
      setError(e.message || "Código não reconhecido");
      // resume scanning after 1.5s
      setTimeout(() => {
        try { scannerRef.current?.resume(); } catch { /* ignore */ }
        setError("");
        setLastCode("");
      }, 1800);
    }
  };

  const submitCheckin = async (ordem) => {
    if (!match) return;
    setSubmitting(true);
    try {
      const res = await adminApi.cursoCheckinRegister(match.ficha.ID_FichaInscricao, ordem);
      if (res.duplicate) toast.warning(`Já tinha feito check-in à sessão ${ordem}.`);
      else toast.success(`✅ ${match.ficha.NomeCompleto} — entrada registada (sessão ${ordem})`);
      // Refresh status to update buttons
      await lookup(match.ficha.CodigoBarras);
    } catch (e) {
      toast.error(e.message || "Erro a registar");
    } finally { setSubmitting(false); }
  };

  const continueScan = async () => {
    setMatch(null);
    setError("");
    setLastCode("");
    try { await scannerRef.current?.resume(); } catch { /* ignore */ }
  };

  const onManual = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    lookup(manualCode.trim().toUpperCase());
  };

  useEffect(() => () => {
    stopScanner();
    // Best-effort cleanup do wake lock se ainda existir
    try { wakeLockRef.current?.release?.(); } catch { /* ignore */ }
  }, []);

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#FAF8F2] flex items-center justify-center">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-[#FAF8F2] pb-12 ${kiosk ? "kiosk-mode" : ""}`} data-testid="admin-curso-checkin" data-kiosk={kiosk ? "1" : "0"}>
      {!kiosk && (
        <header className="px-4 py-3 bg-white border-b border-[#E6E1D6] flex items-center gap-2 sticky top-0 z-10">
          <Link to="/admin" className="text-[#0F1829] hover:opacity-70" data-testid="checkin-back">
            <ChevronLeft size={18} />
          </Link>
          <h1 className="text-sm font-bold uppercase tracking-[0.12em] flex-1">Check-in</h1>
          <button onClick={enterKiosk} title="Modo kiosk (fullscreen + ecrã sempre ligado)"
            data-testid="checkin-kiosk-on"
            className="text-[11px] px-2.5 py-1 border border-[#0F1829] text-[#0F1829] rounded inline-flex items-center gap-1 hover:bg-[#FAF8F2]">
            <Maximize2 size={11} /> Kiosk
          </button>
          {installable && !installed && (
            <button onClick={installPWA} data-testid="checkin-pwa-install"
              className="text-[11px] px-2.5 py-1 bg-[#0A6B2E] text-white rounded inline-flex items-center gap-1">
              <Download size={11} /> Instalar
            </button>
          )}
          <button onClick={logout} title={meEmail} data-testid="checkin-logout"
            className="text-[#6B7280] hover:text-[#0F1829]">
            <LogOut size={16} />
          </button>
        </header>
      )}
      {kiosk && (
        <button onClick={exitKiosk} data-testid="checkin-kiosk-off"
          title="Sair do modo kiosk"
          className="fixed top-3 right-3 z-50 p-2.5 bg-black/40 backdrop-blur text-white rounded-full hover:bg-black/60">
          <Minimize2 size={16} />
        </button>
      )}

      <div className={`max-w-md mx-auto p-4 space-y-3 ${kiosk ? "pt-6" : ""}`}>
        {meEmail && !kiosk && <p className="text-[10px] uppercase tracking-wider text-[#978D78]" data-testid="checkin-me">Operador: {meEmail}</p>}
        {!scanning && !match && (
          <button
            onClick={startScanner}
            data-testid="checkin-start"
            className="w-full p-4 bg-[#0F1829] text-white rounded-md font-semibold text-base inline-flex items-center justify-center gap-2 hover:bg-[#1A2540]">
            <Camera size={18} /> Iniciar leitura
          </button>
        )}

        {/* Scanner viewport */}
        <div className={`${scanning ? "" : "hidden"} bg-black rounded-md overflow-hidden border-4 border-[#0F1829]`}>
          <div id={SCAN_REGION_ID} className="w-full" />
        </div>

        {scanning && !match && (
          <button onClick={stopScanner} className="w-full p-2 text-sm border border-[#E6E1D6] rounded inline-flex items-center justify-center gap-1.5"
            data-testid="checkin-stop">
            <CameraOff size={14} /> Parar
          </button>
        )}

        {/* Manual entry fallback */}
        {!match && (
          <form onSubmit={onManual} className="bg-white border border-[#E6E1D6] rounded-md p-3" data-testid="checkin-manual-form">
            <label className="text-[11px] uppercase tracking-wider text-[#6B7280]">Introduzir código manualmente</label>
            <div className="flex gap-2 mt-1">
              <input
                value={manualCode} onChange={(e) => setManualCode(e.target.value)}
                placeholder="IHTP-..."
                data-testid="checkin-manual-input"
                className="flex-1 px-3 py-2 text-sm border border-[#E6E1D6] rounded focus:outline-none focus:ring-2 focus:ring-[#0F1829]/20" />
              <button type="submit" className="px-3 py-2 text-sm bg-[#0F1829] text-white rounded hover:bg-[#1A2540]"
                data-testid="checkin-manual-submit">OK</button>
            </div>
          </form>
        )}

        {error && (
          <div className="p-3 bg-[#FDE8E8] border border-[#F5A5A5] rounded text-sm text-[#9B1C1C] flex items-start gap-2" data-testid="checkin-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {match && <MatchCard match={match} submitting={submitting} onCheckIn={submitCheckin} onContinue={continueScan} />}
      </div>
    </div>
  );
}

function fmtTime(d) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function MatchCard({ match, submitting, onCheckIn, onContinue }) {
  const { ficha, sessoes, checkins } = match;
  const checkinByOrdem = Object.fromEntries((checkins || []).map((c) => [c.Ordem, c]));
  return (
    <div className="bg-white border border-[#E6E1D6] rounded-md p-4" data-testid="checkin-match">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="text-[#0A6B2E]" size={24} />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-[#978D78]">Inscrição validada</p>
          <p className="text-lg font-bold text-[#0F1829] truncate">{ficha.NomeCompleto}</p>
          <p className="text-xs text-[#6B7280] truncate">{ficha.NomeCurso}</p>
          <p className="text-[10px] text-[#978D78] mt-1 font-mono">{ficha.CodigoBarras}</p>
        </div>
      </div>

      <div className="mt-4 space-y-1.5">
        <p className="text-[11px] uppercase tracking-wider text-[#6B7280]">Sessões — clique para registar entrada</p>
        {(sessoes || []).length === 0 && (
          <button onClick={() => onCheckIn(1)} disabled={submitting} data-testid="checkin-sess-1"
            className="w-full p-3 bg-[#0F1829] text-white rounded font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={16} />}
            Registar entrada
          </button>
        )}
        {(sessoes || []).map((s) => {
          const done = !!checkinByOrdem[s.Ordem];
          return (
            <button
              key={s.Ordem}
              onClick={() => onCheckIn(s.Ordem)}
              disabled={submitting || done}
              data-testid={`checkin-sess-${s.Ordem}`}
              className={`w-full p-3 rounded font-medium text-sm flex items-center justify-between disabled:opacity-60 ${
                done ? "bg-[#E6F4EA] text-[#0A6B2E] border border-[#A6DFB6]"
                     : "bg-[#0F1829] text-white hover:bg-[#1A2540]"
              }`}>
              <span className="flex items-center gap-2">
                {done ? <CheckCircle2 size={16} /> : <span className="font-bold">{s.Ordem}</span>}
                Sessão {s.Ordem}{s.Data ? ` — ${String(s.Data).slice(0, 10)}` : ""}{s.HorarioManha ? ` ${s.HorarioManha}` : ""}
              </span>
              {done && <span className="text-[10px]">{fmtTime(checkinByOrdem[s.Ordem].DataCheckIn)}</span>}
            </button>
          );
        })}
      </div>

      <button onClick={onContinue} data-testid="checkin-continue"
        className="mt-4 w-full p-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]">
        Continuar a ler outros códigos
      </button>
    </div>
  );
}
